import { useEffect, useState } from "react";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import { DataPermissions } from "../../../../classes/enums/PermissionsEnum";
import {
  filterByDateRange,
  formatClinicalDate,
  parseTimestampField,
} from "../../../../classes/utils/DateUtils";
import { filterBySearchTerm } from "../../../../classes/utils/SearchUtils";
import AddNew from "../../../../components/addNew/AddNew";
import { ErrorMessage } from "../../../../components/errorMessage/ErrorMessage";
import FilterAndActionBar from "../../../../components/filterAndActionBar.js/FilterAndActionBar";
import WidgetLoader from "../../../../components/widgetLoader/WidgetLoader";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useAuth } from "../../../../hooks/useAuth";
import { useRouteState } from "../../../../hooks/useRouteState";
import PainAssesmentModal from "../../../common/painAssesment/PainAssesmentModal";
import "../common/PatientWidgetLayout.css";
import usePatientWidgetControlsOffset from "../common/usePatientWidgetControlsOffset";
import "./PainAssessment.css";
import PainAssessmentItem from "./PainAssessmentItem";

const PainAssessment = () => {
  const {
    assessmentsByDate,
    painAssessmentLoading,
    painAssessmentError,
    refreshPainAssessments,
  } = usePatientContext();
  const { dateSettings } = useUser();
  const { showPopup } = useAppContext();
  const { checkPermissions } = useAuth();
  const { params } = useRouteState();
  const { t } = useTranslation();

  const [canImport] = useState(true);
  const [filteredAssessments, setFilteredAssessments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const canDelete = checkPermissions(DataPermissions.DELETE_PAIN_ASSESSMENT);

  useEffect(() => {
    if (assessmentsByDate && Array.isArray(assessmentsByDate)) {
      let filtered = [...assessmentsByDate];

      // Apply search filter - full text search
      filtered = filterBySearchTerm(
        filtered,
        assessment => {
          let text = "";
          const dateField = assessment.createdAt || assessment.date;
          if (dateField) {
            const date = parseTimestampField(dateField);
            if (date) {
              text += date.toString() + " ";
              text +=
                (formatClinicalDate(date, { locale: dateSettings?.locale }) ||
                  "") + " ";
            }
          }
          if (assessment.allPaths?.length) {
            assessment.allPaths.forEach(path => {
              if (path.painLevel != null)
                text += path.painLevel.toString() + " ";
              if (path.painType) text += path.painType + " ";
            });
          }
          return text;
        },
        searchTerm
      );

      // Apply date range filter
      if (dateRange && (dateRange.start || dateRange.end)) {
        filtered = filterByDateRange(
          filtered,
          item => item.createdAt || item.date,
          dateRange.start,
          dateRange.end
        );
      }

      setFilteredAssessments(filtered);
    } else {
      setFilteredAssessments([]);
    }
  }, [assessmentsByDate, searchTerm, dateRange, dateSettings]);

  const handleSearch = term => {
    setSearchTerm(term || "");
  };

  const handleDateRangeChange = (start, end) => {
    setDateRange({ start, end });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDateRange({ start: null, end: null });
  };

  const { controlsRef, shellStyle } = usePatientWidgetControlsOffset();

  const openAddPainAssessment = () => {
    showPopup(<PainAssesmentModal />);
  };

  const renderContent = () => {
    if (painAssessmentLoading) {
      return <WidgetLoader isLoading={true} />;
    }

    if (painAssessmentError) {
      // add translation for the error message of the pain assessment
      return <ErrorMessage message={t("215")} />;
    }

    if (!filteredAssessments || filteredAssessments.length === 0) {
      return (
        <AddNew
          // add translation for the element title
          elementTitle={t("186")}
          onClick={canImport ? openAddPainAssessment : null}
        />
      );
    }

    return (
      <div className="patient-pain-assessment-list">
        {filteredAssessments.map(assessment => (
          <PainAssessmentItem
            key={assessment._id}
            assessment={assessment}
            canDelete={canDelete}
            onAssessmentDeleted={refreshPainAssessments}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className="patient-pain-assessment patient-widget-shell"
      key={params.patientId}
      style={shellStyle}
    >
      <div className="patient-pain-assessment-content patient-widget-content">
        {renderContent()}
      </div>

      <div
        className="patient-pain-assessment-controls patient-widget-controls"
        ref={controlsRef}
      >
        <FilterAndActionBar
          onSearch={handleSearch}
          onDateRangeChange={handleDateRangeChange}
          onClear={clearFilters}
          onAdd={canImport ? openAddPainAssessment : null}
          value={searchTerm}
        />
      </div>
    </div>
  );
};
export default PainAssessment;
